import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ATTACHMENT_MAX_BYTES, ATTACHMENTS_PER_TARGET, type ReportItemDTO } from '@wd/shared';
import { del, errorText } from '../../lib/api';
import { uploadFile } from '../../lib/attachments';
import { AttachButton, AttachmentList } from '../../ui/attachments';
import { MenuList, Popover } from '../../ui/menu';
import { IconButton } from '../../ui/primitives';

// 日报进展上的附件：草稿时可以加、删；提交后只读

export function ItemFiles({ item, editable, showAdd, className }: { item: ReportItemDTO; editable: boolean; showAdd: boolean; className?: string }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['report'] });

  const add = async (files: File[]) => {
    setBusy(true);
    setError(null);
    try {
      for (const f of files) {
        setProgress(0);
        await uploadFile(f, item.id, setProgress);
      }
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
      setProgress(null);
      await refresh();
    }
  };
  const remove = async (id: string) => {
    try {
      await del(`/attachments/${id}`);
    } catch (e) {
      setError(errorText(e));
    }
    await refresh();
  };

  const files = item.attachments ?? [];
  if (!files.length && !(editable && (showAdd || busy)) && !error) return null;
  return (
    <div className={className ?? 'rp-files'}>
      <AttachmentList files={files} onRemove={editable ? (id) => void remove(id) : undefined} />
      {editable && (showAdd || busy || files.length > 0) && <AttachButton count={files.length} busy={busy} progress={progress} onFiles={(f) => void add(f)} />}
      {error && <span className="t-meta c-red">{error}</span>}
    </div>
  );
}

/**
 * 底部输入框里的回形针：先选附到哪个项目（只有一个项目时直接选文件），再选文件。
 * 附件挂在对应的那条进展下面。
 */
export function ComposerAttach({ items }: { items: ReportItemDTO[] }) {
  const qc = useQueryClient();
  const anchor = useRef<HTMLSpanElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const [picking, setPicking] = useState(false);
  const [target, setTarget] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const open = items.filter((i) => (i.attachments?.length ?? 0) < ATTACHMENTS_PER_TARGET);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 6000);
    return () => clearTimeout(t);
  }, [error]);

  const choose = (itemId: string) => {
    setTarget(itemId);
    setPicking(false);
    input.current?.click();
  };

  const upload = async (files: File[]) => {
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      for (const f of files) {
        setProgress(0);
        await uploadFile(f, target, setProgress);
      }
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
      setProgress(null);
      await qc.invalidateQueries({ queryKey: ['report'] });
    }
  };

  return (
    <>
      {error && <span className="t-meta c-red ellipsis" style={{ maxWidth: 240 }}>{error}</span>}
      {busy && <span className="t-meta">上传中{progress != null ? ` ${progress}%` : '…'}</span>}
      <span ref={anchor} style={{ display: 'inline-flex' }}>
      <IconButton
        name="clip"
        label={busy ? '上传中…' : open.length ? `添加附件（图片、视频、PDF、文档，单个不超过 ${Math.round(ATTACHMENT_MAX_BYTES / 1024 / 1024)} MB）` : '每条进展最多 5 个附件'}
        disabled={busy || open.length === 0}
        onClick={() => (open.length === 1 ? choose(open[0]!.id) : setPicking(!picking))}
      />
      </span>
      <Popover anchor={anchor} open={picking} onClose={() => setPicking(false)} width={280} align="end">
        <div className="wd-pop-label">附到哪个项目的进展下面</div>
        <MenuList
          items={open.map((i) => ({
            value: i.id,
            label: i.projectName,
            hint: `${i.projectCode}${i.attachments?.length ? ` · 已有 ${i.attachments.length} 个` : ''}`,
          }))}
          selected={null}
          onPick={choose}
        />
      </Popover>
      <input
        ref={input}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          const item = items.find((i) => i.id === target);
          const room = ATTACHMENTS_PER_TARGET - (item?.attachments?.length ?? 0);
          const list = [...(e.target.files ?? [])].slice(0, room);
          e.target.value = '';
          if (list.length) void upload(list);
        }}
      />
    </>
  );
}
